import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from "react-router-dom";
import FillMissing from "../question/FillMissing";
import SingleAnswer from "../question/SingleAnswer";
import AnswerWithText from "../question/AnswerWithText";
import MultipleAnswers from "../question/MultipleAnswers";
import QuestionInstructionDetail from '../question/QuestionInstructionDetail';

const ReadingComponent = () => {
    const [dividerPosition, setDividerPosition] = useState(50);
    const navigate = useNavigate();
    const [examData, setExamData] = useState([]);
    const { id } = useParams();
    const [answerMap, setAnswerMap] = useState(new Map());

    useEffect(() => {
        if (!id) return;
        fetch(`/exams/${id}/Exam.json`)
            .then((response) => response.json())
            .then((data) => {
                if (Array.isArray(data.reading)) {
                    setExamData(data.reading); // ✅ Ensure it's an array
                } else {
                    console.error("Error: `reading` is not an array", data.listening);
                }
            })
            .catch((error) => console.error("Error loading exam data:", error));
    }, [id]);

    const handleSubmit = (e) => {
        e.preventDefault();

        // Convert Map to an object before passing it to navigate
        const answerObject = Object.fromEntries(answerMap);

        navigate("/submit-result", { state: { answers: answerObject } });
    };

    const handleCalculateScore = (type, questionNumber, userAnswers, correctAnswers) => {
        setAnswerMap(prevMap => {
            const newMap = new Map(prevMap);
            newMap.set(questionNumber, { userAnswers, correctAnswers }); // Update existing or add new
            return newMap;
        });
    };

    const handleMouseDown = (e) => {
        e.preventDefault();
        document.addEventListener("mousemove", handleMouseMove);
        document.addEventListener("mouseup", handleMouseUp);
    };

    const handleMouseMove = (e) => {
        const newDividerPosition = (e.clientX / window.innerWidth) * 100;
        if (newDividerPosition > 20 && newDividerPosition < 80) {
            setDividerPosition(newDividerPosition);
        }
    };

    const handleMouseUp = () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
    };

    return (
        <div className="w-full h-full flex flex-col bg-gray-50">
            {/* Header */}
            <div className="w-full h-[50px] bg-white flex items-center px-4 border-b border-gray-300 justify-between">
                {/* Logo Section */}
                <div className="flex items-center justify-center h-full bg-gray-200 rounded-md px-4">
                    <Link to={"/student-dashboard"}><span className="text-sm text-gray-700">IELTS Virtual Exam</span></Link>
                </div>

                {/* Centered Text */}
                <div className="flex-1 flex justify-center">
                    <p><b>Reading</b></p>
                </div>

                {/* Navigation Links */}
                <div className="flex space-x-4">
                    <Link to={`/writing/${id}`} className="px-3 py-2 bg-blue-500 text-white rounded hover:bg-blue-600">Writing</Link>
                    <Link to={`/listening/${id}`} className="px-3 py-2 bg-green-500 text-white rounded hover:bg-green-600">Listening</Link>
                </div>
            </div>

            {/* Main Content */}
            <div className="w-full flex-1 flex flex-col justify-center items-center">
                <div className="w-full bg-white rounded-lg shadow-lg border border-gray-300 p-6">
                    {examData.length > 0 ? (
                        <div className="h-[80vh] overflow-y-auto">
                            {examData.map((part, partIndex) => (
                                <div key={partIndex} className="mb-6">
                                    {/* Part Header */}
                                    <div className="w-full bg-gray-200 rounded-lg my-4 p-4">
                                        {/* Part Number and Question */}
                                        <div className="text-lg font-semibold text-gray-800">
                                            Part {part.part}<span className="ml-8"></span> {part.question_text}
                                        </div>
                                        {/* Instruction */}
                                        <div className="text-sm text-gray-600 mt-1 whitespace-pre-line">
                                            {part.instruction}
                                        </div>
                                        {/* Conditionally Render Image */}
                                        {part.image && (
                                            <div className="mt-3 flex justify-center">
                                                <img
                                                    src={part.image}
                                                    alt="Question"
                                                    className="max-w-full h-auto rounded-lg shadow-md"
                                                />
                                            </div>
                                        )}
                                    </div>
                                    {/* Content */}
                                    <div className="flex my-4 gap-2 items-center relative w-full h-[500px]">
                                        {/* Paragraph Section */}
                                        <div
                                            className="p-4 rounded-lg shadow-inner flex-1 overflow-y-auto flex flex-col h-full"
                                            style={{ width: `${dividerPosition}%`, maxHeight: "500px" }}
                                        >
                                            <p className="leading-relaxed whitespace-pre-line">{part.paragraph}</p>
                                        </div>


                                        {/* Draggable Vertical Line */}
                                        <div
                                            className="w-[2px] h-full bg-gray-700 cursor-ew-resize"
                                            onMouseDown={handleMouseDown}
                                        ></div>

                                        {/* Questions */}
                                        <div className="p-4 rounded-lg shadow-inner flex-1 overflow-y-auto" style={{ maxHeight: "500px" }}>
                                            {part.questions.map((question, questionIndex) => {
                                                if (question.type === 1) {
                                                    return (
                                                        <FillMissing
                                                            key={questionIndex}
                                                            questionNumber={question.questionNumber}
                                                            questionText={question.question_text}
                                                            instruction={question.instruction}
                                                            paragraph={question.answers.join("\n")}
                                                            correctAnswers={question.correct_answers} // Pass correct answers
                                                            onCalculateScore={handleCalculateScore}
                                                        />
                                                    );
                                                } else if (question.type === 3) {
                                                    return (
                                                        <SingleAnswer
                                                            key={questionIndex}
                                                            questionNumber={question.questionNumber}
                                                            question={question.question}
                                                            answers={question.answers}
                                                            correctAnswer={question.correct_answer}
                                                            onCalculateScore={handleCalculateScore}
                                                        />
                                                    );
                                                } else if (question.type === 0) {
                                                    return (
                                                        <AnswerWithText
                                                            key={questionIndex}
                                                            questionNumber={question.questionNumber}
                                                            question={question.question}
                                                            correctAnswer={question.correctAnswer}
                                                            onCalculateScore={handleCalculateScore}
                                                        />
                                                    );
                                                } else if (question.type === 2) {
                                                    return (
                                                        <MultipleAnswers
                                                            key={questionIndex}
                                                            questionNumber={question.questionNumber}
                                                            question={question.question}
                                                            answers={question.answers}
                                                            correctAnswer={question.correctAnswer}
                                                            onCalculateScore={handleCalculateScore}
                                                        />
                                                    );
                                                } else if (question.type === 6) {
                                                    return (
                                                        <QuestionInstructionDetail
                                                            instruction={question.instruction}
                                                        />
                                                    );
                                                }
                                                return null; // 🚨 Must return null if condition is false to avoid errors
                                            })}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p>Loading...</p>
                    )}
                    {/* Sidebar (Fixed at Bottom-Left) */}
                    <div className="fixed bottom-0 left-0 w-1/4 p-10">
                        <div className="mt-2 flex space-x-4">
                            {examData.map((part, partIndex) => {
                                // Check if all questions in the part are answered
                                const allAnswered = part.questions.every(q => q.type === 6 || answerMap.has(q.questionNumber));

                                return (
                                    <div key={partIndex} className="flex items-end space-x-4">
                                        {/* Part Label with Line Above */}
                                        <div className="flex flex-col items-center">
                                            <div className={`h-1 w-full mb-1 ${allAnswered ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                                            <p className="text-sm font-semibold whitespace-nowrap">Part {part.part}</p>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                    {/* Submit Button */}
                    <div className="px-5 pb-5 flex flex-col items-center w-full">
                        <div className="w-full h-px bg-gray-700 mb-4"></div>
                        <div className="w-full flex justify-end">
                            <button
                                className="w-28 h-12 bg-blue-500 text-white rounded-md flex items-center justify-center cursor-pointer hover:bg-blue-600 transition text-lg"
                                onClick={handleSubmit}
                            >
                                Submit
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ReadingComponent;
